// The Staff and roles screen's server state (spec/07 §6, F-IAM-08/-10/-11, F-ORG-13), as TanStack
// Query hooks over the generated client — `packages/contracts/src/routes/{members,staff-pin}.ts`:
//
//   GET    …/organizations/{o}/members                         the staff              members:read
//   PUT    …/members/{m}/role                                  change a role          members:roles
//   GET    …/members/{m}/limits  ·  PUT …                     a member's comp limit  members:read / members:roles
//   PUT    …/members/{m}/status                                suspend / reinstate    members:remove
//   DELETE …/members/{m}                                       remove                 members:remove
//   DELETE …/members/{m}/pin  (X-Clavian-Venue)               reset a PIN            members:update
//   GET    …/roles     ·  PUT …/roles/{role}/limits           roles and their limits members:read / members:roles
//   GET    …/invitations  ·  POST …  ·  DELETE …/{id}         invitations            members:read / members:invite
//
// All organisation-scoped, so the venue console and the organisation console share them; a role
// reaches its holders at every venue. A PIN is per venue, so resetting one needs a venue in the header.

import type {
  ApiClient,
  ChangeMemberStatus,
  CreateInvitation,
  CreateRole,
  Invitation,
  Member,
  MemberLimits,
  Role,
  RoleLimits,
  UpdateRole,
} from '@clavian/api-client';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '../../api/client';

const PAGE_SIZE = 50;

export const staffKeys = {
  members: (organizationId: string) => ['staff', organizationId, 'members'] as const,
  roles: (organizationId: string) => ['staff', organizationId, 'roles'] as const,
  invitations: (organizationId: string) => ['staff', organizationId, 'invitations'] as const,
  memberLimits: (organizationId: string, memberId: string) => ['staff', organizationId, 'members', memberId, 'limits'] as const,
};

/** The organisation's members, a page at a time (F-IAM-08). */
export function useMembers(organizationId: string) {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: staffKeys.members(organizationId),
    queryFn: ({ pageParam }) =>
      api.listMembers({ path: { organizationId }, query: { limit: PAGE_SIZE, ...(pageParam === undefined ? {} : { cursor: pageParam }) } }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.page.hasMore && last.page.cursor !== null ? last.page.cursor : undefined),
  });
}

/** Every role of the organisation — the built-in ones and its custom roles (F-IAM-11). */
export function useRoles(organizationId: string) {
  const api = useApi();
  return useQuery<readonly Role[]>({
    queryKey: staffKeys.roles(organizationId),
    queryFn: async () => (await api.listRoles({ path: { organizationId } })).data,
  });
}

/** Create a custom role — a name and the permissions it combines (F-IAM-11, `members:roles`). */
export function useCreateRole(organizationId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateRole) => api.createRole({ path: { organizationId }, body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: staffKeys.roles(organizationId) }),
  });
}

/** Replace a custom role's permission set (F-IAM-11, `members:roles`); built-in roles are fixed. */
export function useUpdateRole(organizationId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ role, body }: { role: string; body: UpdateRole }) => api.updateRole({ path: { organizationId, role }, body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: staffKeys.roles(organizationId) }),
  });
}

/** Delete a custom role (F-IAM-11, `members:roles`). Refused by the server when a member still holds it. */
export function useDeleteRole(organizationId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (role: string) => api.deleteRole({ path: { organizationId, role } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: staffKeys.roles(organizationId) }),
  });
}

/** The organisation's invitations, newest first (F-IAM-08). */
export function useInvitations(organizationId: string) {
  const api = useApi();
  return useQuery<readonly Invitation[]>({
    queryKey: staffKeys.invitations(organizationId),
    queryFn: async () => (await api.listInvitations({ path: { organizationId } })).data,
  });
}

/** Invite a member by phone or email, naming a role and the venues they may work at (F-IAM-08). */
export function useCreateInvitation(organizationId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateInvitation) => api.createInvitation({ path: { organizationId }, body }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: staffKeys.invitations(organizationId) }),
  });
}

/** Withdraw a pending invitation (F-IAM-08). */
export function useRevokeInvitation(organizationId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) => api.revokeInvitation({ path: { organizationId, invitationId } }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: staffKeys.invitations(organizationId) }),
  });
}

/** Change a member's organisation role (F-ORG-13, `members:roles`). */
export function useChangeMemberRole(organizationId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ memberId, role }: { memberId: string; role: string }) => api.changeMemberRole({ path: { organizationId, memberId }, body: { role } }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: staffKeys.members(organizationId) }),
  });
}

/** Suspend or reinstate a member (F-ORG-13, `members:remove`). Suspending keeps their role and venues. */
export function useChangeMemberStatus(organizationId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ memberId, status }: { memberId: string; status: ChangeMemberStatus['status'] }) =>
      api.changeMemberStatus({ path: { organizationId, memberId }, body: { status } }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: staffKeys.members(organizationId) }),
  });
}

/** Remove a member from the organisation's staff (F-ORG-13, `members:remove`). */
export function useRemoveMember(organizationId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (memberId: string) => api.removeMember({ path: { organizationId, memberId } }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: staffKeys.members(organizationId) }),
  });
}

/** Change a role's limits (F-ORG-13, `members:roles`). The lower of the limit and the setting wins. */
export function useChangeRoleLimits(organizationId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ role, limits }: { role: string; limits: RoleLimits }) => api.changeRoleLimits({ path: { organizationId, role }, body: limits }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: staffKeys.roles(organizationId) }),
  });
}

/** A member's own comp limit, in place of their role's (spec/07 §6.4); `comp: null` follows the role. */
export function useMemberLimits(organizationId: string, memberId: string, enabled = true) {
  const api = useApi();
  return useQuery<MemberLimits>({
    queryKey: staffKeys.memberLimits(organizationId, memberId),
    queryFn: () => api.getMemberLimits({ path: { organizationId, memberId } }),
    enabled,
  });
}

/** Set or clear a member's own comp limit (`members:roles`; "no limit" is an owner's to give). */
export function useChangeMemberLimits(organizationId: string, memberId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: MemberLimits) => api.changeMemberLimits({ path: { organizationId, memberId }, body }),
    onSuccess: (saved) => queryClient.setQueryData(staffKeys.memberLimits(organizationId, memberId), saved),
  });
}

/** Reset a member's PIN at this venue (F-IAM-10, `members:update`). A PIN is set for one venue. */
export function useResetStaffPin(organizationId: string, venueId: string | undefined) {
  const api = useApi();
  return useMutation({
    mutationFn: (memberId: string) => resetPin(api, organizationId, venueId, memberId),
  });
}

function resetPin(api: ApiClient, organizationId: string, venueId: string | undefined, memberId: string) {
  if (venueId === undefined) throw new Error('Resetting a PIN needs a venue.');
  return api.resetStaffPin({ path: { organizationId, memberId }, headerParams: { 'x-clavian-venue': venueId } });
}

/** One line for a member: their name, or the contact the invitation went to, or the id. */
export function memberName(member: Member): string {
  return member.name ?? member.phoneNumber ?? member.email ?? member.id;
}
